import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = 'https://admin.afriperx.online';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  // Create axios instances with interceptors for dynamic token handling
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    return instance;
  }, []);

  const adminApi = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('adminToken');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    return instance;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Check user auth
      if (token) {
        try {
          const res = await api.get('/user/me');
          setUser(res.data);
        } catch (error) {
          console.error('User auth error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      
      // Check admin auth
      if (adminToken) {
        try {
          // Validate admin token by checking stats endpoint
          await adminApi.get('/admin/stats');
          // Token is valid, parse admin info from token
          const payload = JSON.parse(atob(adminToken.split('.')[1]));
          setAdmin({ id: payload.user_id, role: payload.role });
        } catch (error) {
          console.error('Admin auth error:', error);
          localStorage.removeItem('adminToken');
          setAdminToken(null);
        }
      }
      
      setLoading(false);
    };
    initAuth();
  }, [token, adminToken, api, adminApi]);

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    return res.data;
  };

  const verifyOtp = async (mobile, otp) => {
    const res = await axios.post(`${API}/auth/verify-otp`, { mobile, otp });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return res.data;
  };

  const login = async (mobile, password) => {
    const res = await axios.post(`${API}/auth/login`, { mobile, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return res.data;
  };

  const adminLogin = async (email, password) => {
    const res = await axios.post(`${API}/admin/login`, { email, password });
    const { token: newToken, admin: adminData } = res.data;
    localStorage.setItem('adminToken', newToken);
    setAdminToken(newToken);
    setAdmin(adminData);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const adminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setAdmin(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await api.get('/user/me');
        setUser(res.data);
      } catch (error) {
        console.error('Refresh error:', error);
      }
    }
  };

  const value = {
    user,
    admin,
    token,
    adminToken,
    loading,
    api,
    adminApi,
    register,
    verifyOtp,
    login,
    adminLogin,
    logout,
    adminLogout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
